import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { RichTextContent } from '~/components';
import type { MarketFact } from '~/features/shared/market-content';
import { MarketFactGrid } from '~/features/shared/market-content';
import { dateStringFormatter } from '~/features/utils';
import { b2bSideCopy } from '../../b2bSideCopy';
import { useMsaasListingSearchRef } from '../../marketSelectors';
import { useSearchSide } from '../searchSide';
import { ShiftDistribution } from './ShiftDistribution';

const TAGS = CT.listingSearch.contractOverview;

export const ContractOverview = () => {
  const { msaasListingsSearchRef } = useMsaasListingSearchRef();
  const copy = b2bSideCopy(useSearchSide());
  const context = useSelector(msaasListingsSearchRef, (state) => state?.context);
  if (!msaasListingsSearchRef || !context) return null;
  const { listing } = context;

  const calculateContractLength = (
    startDate: string | null | undefined,
    endDate: string | null | undefined
  ) => {
    if (!endDate || !startDate) {
      return 'Ongoing';
    }
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const COVERAGE_DETAILS = [
    {
      label: copy.ftesRequiredLabel,
      value: listing?.detail?.numOfFtes,
      unit: 'Full-Time Equivalents',
      cyTag: TAGS.ftesRequired,
    },
    {
      label: 'Shifts Per Year',
      value: listing?.detail?.fteShiftsPerYear,
      unit: 'Shifts',
      cyTag: TAGS.shiftsPerYear,
    },
    {
      label: 'Hours Per Shift',
      value: listing?.detail?.hoursPerShift,
      unit: 'Hours',
      cyTag: TAGS.hoursPerShift,
    },
    {
      label: 'Total Shifts',
      value: listing?.detail?.numOfShifts,
      unit: 'Shifts',
      cyTag: TAGS.totalShifts,
    },
  ];

  const CONTRACT_PERIOD = [
    {
      label: 'Start Date',
      value: dateStringFormatter(listing?.detail?.startDate || '', 'NUMERIC'),
      cyTag: TAGS.startDate,
    },
    {
      label: 'End Date',
      value: listing?.detail?.endDate
        ? dateStringFormatter(listing?.detail?.endDate || '', 'NUMERIC')
        : 'N/A',
      cyTag: TAGS.endDate,
    },
    {
      label: 'Contract Length',
      value: calculateContractLength(listing?.detail?.startDate, listing?.detail?.endDate),
      cyTag: TAGS.contractLength,
    },
    {
      label: 'Renewable',
      value: listing?.isRenewable ? 'Yes' : 'No',
      cyTag: TAGS.renewable,
    },
  ];
  const coverageFacts: MarketFact[] = COVERAGE_DETAILS.map((detail) => ({
    label: detail.label,
    value: `${detail.value ?? '—'} ${detail.unit}`,
    cyTag: detail.cyTag,
  }));
  const contractPeriodFacts: MarketFact[] = CONTRACT_PERIOD.map((detail) => ({
    label: detail.label,
    value: detail.value ?? '—',
    cyTag: detail.cyTag,
  }));
  const workLocation =
    listing?.workLocation === 'ONSITE'
      ? 'Onsite'
      : listing?.workLocation === 'REMOTE'
        ? 'Remote'
        : listing?.workLocation === 'PARTIAL_REMOTE'
          ? 'Hybrid'
          : '—';

  return (
    <div className="text-left">
      <h3 className="text-xl font-bold leading-snug text-content-primary mb-4 mt-6">
        Listing Description
      </h3>
      <RichTextContent
        value={listing?.listingDescription}
        className="mb-6 text-[1rem] font-normal leading-relaxed text-content-secondary [overflow-wrap:anywhere] @min-[1400px]/b2b-search:text-lg"
        dataCy={TAGS.listingDescription}
      />
      <div className="mb-6 border-b border-grey-900 pb-4" data-cy={TAGS.totalContractValue}>
        <p className="text-sm font-semibold leading-5 text-content-secondary">
          Total Contract Value
        </p>
        <p className="mt-1 text-xl font-semibold leading-snug text-primary-50">
          {Number.isFinite(Number(listing?.detail?.totalContractValue))
            ? `$${Number(listing?.detail?.totalContractValue).toLocaleString('en-US')}`
            : '—'}
        </p>
      </div>
      <h3 className="text-xl font-bold leading-snug text-content-primary mb-4">
        {copy.coverageHeading}
      </h3>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,240px),1fr))] gap-6">
        <div>
          <p className="mb-3 text-sm font-semibold text-content-secondary">Coverage Details</p>
          <MarketFactGrid facts={coverageFacts} density="comfortable" />
        </div>
        <div>
          <p className="mb-3 text-sm font-semibold text-content-secondary">Contract Period</p>
          <MarketFactGrid facts={contractPeriodFacts} density="comfortable" />
        </div>
      </div>
      <ShiftDistribution />
      <h3 className="text-xl font-bold leading-snug text-content-primary mb-4 mt-6">
        Location and Facilities
      </h3>

      <div className="space-y-4 border-t border-grey-900 pt-4">
        <div>
          <p className="text-sm font-semibold leading-5 text-content-secondary">
            Work Location Type
          </p>
          <p className="mt-1 text-[1rem] font-normal leading-6 text-content-primary">
            {workLocation}
          </p>
        </div>
        <div>
          <p className="mb-2 text-sm font-semibold leading-5 text-content-secondary">Facilities</p>
          {listing?.facilities.length === 0 ? (
            <p className="text-[1rem] font-normal leading-6 [overflow-wrap:anywhere]">
              No facilities specified
            </p>
          ) : (
            listing?.facilities.map((facility) => (
              <p
                key={facility.id}
                className="text-[1rem] font-normal leading-6 [overflow-wrap:anywhere]"
              >
                {facility?.facilityName},
              </p>
            ))
          )}
        </div>
        <div>
          <p className="mb-2 text-sm font-semibold leading-5 text-content-secondary">States</p>
          {listing?.stateCodes.length === 0 ? (
            <p className="text-[1rem] font-normal leading-6 [overflow-wrap:anywhere]">
              No states specified
            </p>
          ) : (
            <div data-cy={TAGS.states}>
              {listing?.stateCodes.map((state) => (
                <p
                  key={state}
                  className="text-[1rem] font-normal leading-6 [overflow-wrap:anywhere]"
                >
                  {state}
                </p>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
